import React, { useEffect } from "react";
import { useLang } from "../i18n/LanguageContext";
import { COMPANY } from "../data/content";

const DOMAIN = "https://www.plasto-nord.com";
const OG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/43ead353-3375-4845-9bdd-03b215d7138b/images/bc53d532c37a345fa35b68de762090d7a2ca3f69996471dab82ac51414992e3d.png";

export function Seo({ title, description, path = "/", image = OG_IMAGE, schema, breadcrumbs }) {
  const { lang } = useLang();
  useEffect(() => {
    document.title = title;
    document.documentElement.lang = lang;
    const canonical = DOMAIN + path;

    const setMeta = (key, content, attr = "name") => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:image", image, "property");
    setMeta("og:site_name", COMPANY.name, "property");
    setMeta("og:locale", lang === "de" ? "de_DE" : "en_US", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", canonical);

    const setAlt = (hreflang) => {
      let a = document.head.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
      if (!a) { a = document.createElement("link"); a.setAttribute("rel", "alternate"); a.setAttribute("hreflang", hreflang); document.head.appendChild(a); }
      a.setAttribute("href", canonical);
    };
    setAlt("de-DE");
    setAlt("x-default");

    const postalAddress = { "@type": "PostalAddress", streetAddress: COMPANY.street, postalCode: COMPANY.zip, addressLocality: COMPANY.city, addressCountry: "DE" };
    const org = {
      "@context": "https://schema.org", "@type": "Organization", "@id": DOMAIN + "/#organization",
      name: COMPANY.name, url: DOMAIN, logo: DOMAIN + "/logo-mark.png", image,
      email: COMPANY.email, telephone: COMPANY.phone, vatID: "DE263279921",
      address: postalAddress,
      sameAs: [COMPANY.portalUrl],
    };
    const website = {
      "@context": "https://schema.org", "@type": "WebSite", "@id": DOMAIN + "/#website",
      url: DOMAIN, name: COMPANY.name, inLanguage: "de-DE",
      publisher: { "@id": DOMAIN + "/#organization" },
      potentialAction: {
        "@type": "SearchAction",
        target: { "@type": "EntryPoint", urlTemplate: DOMAIN + "/wissen?q={search_term_string}" },
        "query-input": "required name=search_term_string",
      },
    };
    const localBusiness = {
      "@context": "https://schema.org", "@type": "LocalBusiness", "@id": DOMAIN + "/#localbusiness",
      name: COMPANY.name, url: DOMAIN, image, telephone: COMPANY.phone, email: COMPANY.email,
      address: postalAddress, priceRange: "$$",
      openingHoursSpecification: [{ "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "17:00" }],
    };
    const graph = [org, website, localBusiness];
    if (Array.isArray(schema)) schema.forEach((s) => s && graph.push(s));
    else if (schema) graph.push(schema);
    if (breadcrumbs && breadcrumbs.length) {
      graph.push({
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({ "@type": "ListItem", position: i + 1, name: b.name, item: DOMAIN + b.path })),
      });
    }
    const id = "pn-jsonld";
    let s = document.getElementById(id);
    if (!s) { s = document.createElement("script"); s.id = id; s.type = "application/ld+json"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(graph);
  }, [title, description, path, image, schema, breadcrumbs, lang]);
  return null;
}
